import { FC, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { device } from '../breakpoints'
import { VscClose } from 'react-icons/vsc'
import { MdOutlineKeyboardArrowDown } from 'react-icons/md'

interface FiltersProps {
  data: any
  onFilterChange: (filters: any) => void
}

// get the liste on unique mainSector from the data
const getValues = (key: any, data: any) => {
  const mainSectors = new Set()
  data.forEach((item: any) => {
    mainSectors.add(item[key])
  })

  // return an array of objects with the value and label properties
  return Array.from(mainSectors)
    .map((mainSector: any) => {
      return { value: mainSector, label: mainSector }
    })
    .sort((a: any, b: any) => {
      return a.label.localeCompare(b.label)
    })
}

// Return the list of ages, every 5 years
const getAges = () => {
  const ages = []
  for (let i = 20; i < 40; i += 5) {
    ages.push({ value: i, label: `${i} ans à ${i + 5} ans` })
  }
  return ages
}

const Filters: FC<FiltersProps> = ({ data, onFilterChange }): ReactElement => {
  const [filters, setFilters] = useState<any>(new Map())

  const onSelectChange = (value: any, filterName: string) => {
    const newFilters = new Map(filters)

    if (value) {
      if (!newFilters.has(value.label)) {
        newFilters.set(
          filterName,
          filterName === 'age' ? parseInt(value.value) : value.value
        )
        setFilters(newFilters)
        return
      }
    } else {
      newFilters.delete(filterName)
      setFilters(newFilters)
      return
    }
  }

  useEffect(() => {
    onFilterChange(filters)
  }, [filters])

  return (
    <Container>
      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'sector'}
        placeholder='Secteur'
        options={getValues('mainSector', data)}
      />

      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'age'}
        placeholder='Âge'
        options={getAges()}
      />

      <Select
        onSelectChange={(v: any, n: any) => {
          onSelectChange(v, n)
        }}
        name={'location'}
        placeholder='Domicile'
        options={getValues('location', data)}
      />
    </Container>
  )
}

const Container = styled.div`
  font-family: var(--font-sans-serif);
  font-color: ${({ theme }) => theme.textColor};

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    column-count: 1;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    column-count: 3;
  }
`

const SelectContainer = styled.div`
  position: relative;
  font-family: var(--font-sans-serif);
  font-size: 1.6rem;
  margin-bottom: 12px;
`

const CustomSelect = styled.select<{ placeholderSelected: boolean }>`
  // A reset of styles, including removing the default dropdown arrow
  appearance: none;
  // Additional resets for further consistency
  background-color: transparent;
  border: none;
  padding: 0 1em 0 0;
  margin: 0;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  cursor: inherit;
  line-height: inherit;
  outline: none;

  font-family: var(--font-sans-serif);
  font-size: 1.6rem;
  width: 100%;
  min-width: 15ch;
  padding: 15px 20px;
  cursor: pointer;
  line-height: 1.1;
  background-color: ${({ theme }) => theme.buttonColor};
  // background-image: linear-gradient(to top, #f9f9f9, #fff 33%);
  color: ${({ theme, placeholderSelected }) =>
    placeholderSelected ? theme.textGrey : theme.buttonTextColor};

  &:-ms-expand {
    display: none;
  }
`

const ResetFilter = styled.div`
  position: absolute;
  top: 0;
  right: 42px;
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 2.2rem;

  &:after {
    content: '';
    width: 1px;
    height: 50%;
    background-color: rgba(0, 0, 0, 0.05);
    position: absolute;
    top: 0;
    transform: translateY(50%);
    right: 0;
  }
`

const ArrowContainer = styled.div`
position: absolute;
top: 0;
right: 0;
width: 50px;
font-size: 2.2rem;
cursor: pointer;
pointer-events: none;
height: 100%;
display: flex;
align-items: center;
justify-content: center;
`

export default Filters

const Select = ({
  onSelectChange,
  name,
  options,
  placeholder,
}: {
  onSelectChange: (v: any, l: any) => void
  name: string
  options: any
  placeholder: string
}) => {
  const [value, setValue] = useState<any>('placeholder')

  const onChange = (value: string | null) => {
    setValue(value)
  }

  return (
    <SelectContainer>
      <CustomSelect
        value={value}
        placeholderSelected={value === 'placeholder'}
        onChange={(e: any) => {
          onChange(e.target.value)
          onSelectChange({ value: e.target.value, label: e.target.text }, name)
        }}
      >
        <option value='placeholder' disabled>
          {placeholder}
        </option>
        {options.map((o: any) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </CustomSelect>
      {value !== 'placeholder' && (
        <ResetFilter
          onClick={() => {
            onChange('placeholder')
            onSelectChange(null, name)
          }}
        >
          <VscClose />
        </ResetFilter>
      )}
      <ArrowContainer>
        <MdOutlineKeyboardArrowDown />
      </ArrowContainer>
    </SelectContainer>
  )
}
